import { useEffect, useState } from 'react'

import { apiClient, ApiError } from '@/lib/apiClient'

/**
 * Le bulletin de passage — voter depuis le QR posé sur la table.
 *
 * Le carré imprimé est le même pour toute la salle : il ne peut porter aucun
 * jeton personnel. Le convive qui le scanne demande donc son propre bulletin au
 * serveur, puis le **garde dans son navigateur**. C'est ce stockage, et lui
 * seul, qui l'empêche de recommencer en rouvrant la page : sans lui, chaque
 * rafraîchissement fabriquerait une voix de plus.
 *
 * Le bulletin ne vaut que pour le vote — il n'ouvre pas la porte, et il ne
 * donne accès à aucune commande.
 */
const STORAGE_KEY = 'masked-night.ballot'
/** Même cadence que la liste des scrutins (`useVotingCategories`). */
const RETRY_MS = 20_000

export interface Ballot {
  token: string
  attendee_id: string
}

/** Le bulletin déjà en main, s'il y en a un. */
function read(eventSlug: string): Ballot | null {
  try {
    const raw = window.localStorage.getItem(`${STORAGE_KEY}.${eventSlug}`)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as Ballot).token === 'string' &&
      typeof (parsed as Ballot).attendee_id === 'string'
    ) {
      return parsed as Ballot
    }
    return null
  } catch {
    // Navigation privée, stockage plein, ou JSON abîmé : on repart de zéro
    // plutôt que de casser la page de quelqu'un qui veut juste voter.
    return null
  }
}

/** Oublie un bulletin que le serveur ne reconnaît plus. */
export function forgetBallot(eventSlug: string): void {
  try {
    window.localStorage.removeItem(`${STORAGE_KEY}.${eventSlug}`)
  } catch {
    // Rien à oublier si le stockage est inaccessible.
  }
}

function write(eventSlug: string, ballot: Ballot): void {
  try {
    window.localStorage.setItem(`${STORAGE_KEY}.${eventSlug}`, JSON.stringify(ballot))
  } catch {
    // Le vote fonctionnera quand même pour cette page — il ne survivra
    // simplement pas à un rechargement. Mieux que pas de vote du tout.
  }
}

/** Le bulletin de passage que ce navigateur garde déjà, sans en ouvrir un. */
export function storedBallot(eventSlug: string | undefined): Ballot | null {
  return eventSlug ? read(eventSlug) : null
}

// ── Un même navigateur, deux identités ─────────────────────────────
//
// Quand le vote n'est pas réservé aux présents, une même personne peut
// arriver par son lien de billet ET par le QR de sa table. Le navigateur se
// souvient des deux ; dès qu'il les a vues toutes les deux, il demande au
// serveur de les relier, et le quota de voix s'applique à leur réunion.
//
// Ce n'est qu'un garde-fou à l'échelle d'UN navigateur : une fenêtre privée,
// un autre téléphone ou des données effacées le contournent. Le serveur le
// dit aussi (`VotingService`, « Un même navigateur, deux identités ») ; la
// seule barrière complète reste « Réservé aux présents ».

const LINK_KEY = 'masked-night.vote-link'

/** Retient le lien personnel ouvert dans ce navigateur. */
export function rememberPersonalLink(eventSlug: string | undefined, link: Ballot): void {
  if (!eventSlug) return
  try {
    window.localStorage.setItem(`${LINK_KEY}.${eventSlug}`, JSON.stringify(link))
  } catch {
    // Stockage indisponible : le rapprochement ne se fera pas, le vote si.
  }
}

/** Le lien personnel déjà ouvert dans ce navigateur, s'il y en a un. */
export function personalLink(eventSlug: string | undefined): Ballot | null {
  if (!eventSlug) return null
  try {
    const raw = window.localStorage.getItem(`${LINK_KEY}.${eventSlug}`)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as Ballot).token === 'string' &&
      typeof (parsed as Ballot).attendee_id === 'string'
    ) {
      return parsed as Ballot
    }
    return null
  } catch {
    return null
  }
}

/**
 * Relie, côté serveur, le billet et le bulletin de passage vus par ce
 * navigateur. Une fois par paire ; sans effet visible, sinon que la page
 * relit ensuite ce qu'il reste de voix.
 */
export function useVoteLink(
  holder: Ballot | null,
  guest: Ballot | null,
  onLinked: () => void,
): void {
  const pair = holder && guest ? `${holder.attendee_id}:${guest.attendee_id}` : null
  const [done, setDone] = useState<string | null>(null)

  useEffect(() => {
    if (!pair || !holder || !guest || done === pair) return
    let cancelled = false
    const query = `token=${encodeURIComponent(holder.token)}&attendee_id=${encodeURIComponent(holder.attendee_id)}`
    apiClient
      .post<{ linked_to: string | null }>(`/me/voting/link?${query}`, {
        guest_token: guest.token,
        guest_attendee_id: guest.attendee_id,
      })
      .then(() => {
        if (cancelled) return
        setDone(pair)
        onLinked()
      })
      .catch(() => {
        // Lien périmé, bulletin oublié : rien à relier. On ne réessaie pas
        // en boucle — et le vote, lui, reste possible.
        if (!cancelled) setDone(pair)
      })
    return () => {
      cancelled = true
    }
    // `onLinked` change à chaque rendu ; la paire suffit à décider.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pair, done])
}

export type BallotState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; ballot: Ballot }
  /** Le vote reste réservé aux personnes contrôlées à l'entrée. */
  | { status: 'closed' }
  | { status: 'error'; message: string }

/**
 * Récupère — ou ouvre — le bulletin de ce navigateur.
 *
 * N'est déclenché que sur demande explicite (`enabled`) : quelqu'un qui arrive
 * avec son lien personnel n'a rien à demander, et ouvrir un bulletin anonyme
 * pour lui créerait un votant fantôme dans les compteurs.
 */
export function useGuestBallot(slug: string | undefined, enabled: boolean): BallotState {
  const [state, setState] = useState<BallotState>({ status: 'idle' })
  // Relance la demande quand le vote n'était pas encore ouvert.
  const [attempt, setAttempt] = useState(0)

  // Le QR de la table est souvent scanné AVANT que le MC n'ouvre le vote — au
  // dîner, par curiosité. La page disait alors « pas encore ouvert » et en
  // restait là : il fallait deviner qu'il faudrait la recharger au moment de
  // l'annonce. Elle redemande désormais son bulletin toute seule, au même
  // rythme que la liste des scrutins.
  useEffect(() => {
    if (state.status !== 'closed') return
    const timer = window.setTimeout(() => setAttempt((value) => value + 1), RETRY_MS)
    return () => window.clearTimeout(timer)
  }, [state.status, attempt])

  useEffect(() => {
    if (!slug || !enabled) return

    const existing = read(slug)
    if (existing) {
      setState({ status: 'ready', ballot: existing })
      return
    }

    let cancelled = false
    // Une relance ne repasse pas par l'écran de chargement : la page « pas
    // encore ouvert » reste affichée jusqu'à ce que le bulletin arrive.
    setState((current) => (current.status === 'closed' ? current : { status: 'loading' }))
    apiClient
      .post<Ballot>(`/public/events/${slug}/voting/ballot`)
      .then((ballot) => {
        if (cancelled) return
        write(slug, ballot)
        setState({ status: 'ready', ballot })
      })
      .catch((cause: unknown) => {
        if (cancelled) return
        if (cause instanceof ApiError && cause.code === 'CHECKIN_REQUIRED') {
          setState({ status: 'closed' })
          return
        }
        setState({
          status: 'error',
          message: cause instanceof ApiError ? cause.message : String(cause),
        })
      })

    return () => {
      cancelled = true
    }
  }, [slug, enabled, attempt])

  return state
}
